import React, { useEffect, useRef, useState } from "react";
import AppDatePicker from "../Common/AppDatePicker";
import {
  IoCheckmark,
  IoChevronDown,
  IoCloseCircleOutline,
} from "react-icons/io5";

const recurrencePeriods = [
  "Week",
  "Month",
  "Quarter",
  "Semester",
  "Annual",
  "Other",
];

const RecurringInvoicePopup = ({
  show,
  onClose,
  onConfirm,
  recurringFrom,
  setRecurringFrom,
  recurringTo,
  setRecurringTo,
  recurringPeriod,
  setRecurringPeriod,
  customRecurringDays,
  setCustomRecurringDays,
  isRecurring,
  onRemove,
}) => {
  const [draftFrom, setDraftFrom] = useState(null);
  const [draftTo, setDraftTo] = useState(null);
  const [draftPeriod, setDraftPeriod] = useState("Week");
  const [draftDays, setDraftDays] = useState("");
  const [errors, setErrors] = useState({});
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
  const periodDropdownRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    setDraftFrom(recurringFrom || null);
    setDraftTo(recurringTo || null);
    setDraftPeriod(recurringPeriod || "Week");
    setDraftDays(customRecurringDays || "");
    setErrors({});
    setPeriodMenuOpen(false);
  }, [
    show,
    recurringFrom,
    recurringTo,
    recurringPeriod,
    customRecurringDays,
  ]);

  useEffect(() => {
    if (!show || !periodMenuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (
        periodDropdownRef.current &&
        !periodDropdownRef.current.contains(event.target)
      ) {
        setPeriodMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setPeriodMenuOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [show, periodMenuOpen]);

  if (!show) return null;

  const handleConfirm = () => {
    const nextErrors = {};

    if (!draftFrom) nextErrors.from = "Select a start date";
    if (!draftTo) nextErrors.to = "Select an end date";
    if (draftFrom && draftTo && draftFrom > draftTo) {
      nextErrors.to = "End date must be on or after the start date";
    }
    if (
      draftPeriod === "Other" &&
      (!Number.isInteger(Number(draftDays)) || Number(draftDays) <= 0)
    ) {
      nextErrors.days = "Enter a positive whole number";
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setRecurringFrom(draftFrom);
    setRecurringTo(draftTo);
    setRecurringPeriod(draftPeriod);
    setCustomRecurringDays(draftPeriod === "Other" ? draftDays : "");
    onConfirm(draftFrom, draftTo, draftPeriod, draftDays);
  };

  return (
    <section className="recurrence-popup-overlay">
      <div
        className="recurrence-popup"
        role="dialog"
        aria-modal="true"
        aria-label="Invoice recurrence"
      >
        <div className="recurrence-popup-header">
          <span>Time of recurrence</span>
          <button
            type="button"
            className="recurrence-close-btn"
            onClick={onClose}
            aria-label="Close recurrence popup"
          >
            <IoCloseCircleOutline size={21} />
          </button>
        </div>

        <div className="recurrence-row">
          <label className="recur-title">From</label>
          <div className="recurrence-date-row">
            <div className="recurrence-field">
              <AppDatePicker
                selected={draftFrom}
                onChange={(date) => {
                  setDraftFrom(date);
                  setErrors((current) => ({ ...current, from: "" }));
                }}
                placeholderText="DD.MM.YYYY"
                dateFormat="dd.MM.yyyy"
                inputClassNames={`recurrence-date-input ${
                  errors.from ? "has-error" : ""
                }`}
              />
              {errors.from && (
                <span className="recurrence-error">{errors.from}</span>
              )}
            </div>
            <label className="recur-title">to</label>
            <div className="recurrence-field">
              <AppDatePicker
                selected={draftTo}
                onChange={(date) => {
                  setDraftTo(date);
                  setErrors((current) => ({ ...current, to: "" }));
                }}
                placeholderText="DD.MM.YYYY"
                dateFormat="dd.MM.yyyy"
                inputClassNames={`recurrence-date-input ${
                  errors.to ? "has-error" : ""
                }`}
                minDateForPlanBoard={draftFrom || undefined}
              />
              {errors.to && (
                <span className="recurrence-error">{errors.to}</span>
              )}
            </div>
          </div>
        </div>

        <span className="recurrence-title">Period of recurrence</span>
        <div className="recurrence-period-row">
          <label className="recur-title">Every</label>
          <div className="recurrence-period-dropdown" ref={periodDropdownRef}>
            <button
              type="button"
              className={`recurrence-period-trigger ${
                periodMenuOpen ? "open" : ""
              }`}
              onClick={() => setPeriodMenuOpen((current) => !current)}
              aria-haspopup="listbox"
              aria-expanded={periodMenuOpen}
            >
              <span>{draftPeriod}</span>
              <IoChevronDown className="recurrence-period-chevron" size={17} />
            </button>

            {periodMenuOpen && (
              <div
                className="recurrence-period-options"
                role="listbox"
                aria-label="Period of recurrence"
              >
                {recurrencePeriods.map((period) => {
                  const selected = draftPeriod === period;

                  return (
                    <button
                      type="button"
                      key={period}
                      role="option"
                      aria-selected={selected}
                      className={`recurrence-period-option ${
                        selected ? "selected" : ""
                      }`}
                      onClick={() => {
                        setDraftPeriod(period);
                        setErrors((current) => ({
                          ...current,
                          days: "",
                        }));
                        setPeriodMenuOpen(false);
                      }}
                    >
                      <span>{period}</span>
                      {selected && <IoCheckmark size={17} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {draftPeriod === "Other" && (
          <div className="recurrence-period-row">
            <label className="recur-title">Days</label>
            <div className="recurrence-field">
              <input
                type="number"
                min="1"
                step="1"
                className={`recurrence-days-input ${
                  errors.days ? "has-error" : ""
                }`}
                value={draftDays}
                onChange={(event) => {
                  setDraftDays(event.target.value);
                  setErrors((current) => ({ ...current, days: "" }));
                }}
                placeholder="Number of days"
              />
              {errors.days && (
                <span className="recurrence-error">{errors.days}</span>
              )}
            </div>
          </div>
        )}

        <div className="recurrence-actions">
          {isRecurring && (
            <button
              type="button"
              className="remove-recurring-btn"
              onClick={onRemove}
            >
              Remove recurrence
            </button>
          )}
          <button
            type="button"
            className="make-recurring-btn"
            onClick={handleConfirm}
          >
            Make
          </button>
        </div>
      </div>
    </section>
  );
};

export default RecurringInvoicePopup;
